import React, { useState, useMemo, useRef } from 'react';
import { X, Search, SlidersHorizontal, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { filterCatalog } from '../../utils/exerciseCatalog';
import { ExerciseFilterView } from './ExerciseFilterView';
import { ExerciseDemoButton } from '../ExerciseDemoButton';
import type { ExerciseCatalogEntry } from '../../types';

interface ExercisePickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseCatalog: ExerciseCatalogEntry[];
  onSelect: (entry: ExerciseCatalogEntry) => void;
  onCreateCustom: (name: string) => Promise<ExerciseCatalogEntry | null>;
}

export function ExercisePickerDrawer({
  isOpen,
  onClose,
  exerciseCatalog,
  onSelect,
  onCreateCustom,
}: ExercisePickerDrawerProps) {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [muscleFilters, setMuscleFilters] = useState<string[]>([]);
  const [equipmentFilters, setEquipmentFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [creating, setCreating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const totalFilters = muscleFilters.length + equipmentFilters.length;

  const uniqueMuscles = useMemo(() => {
    const set = new Set<string>();
    exerciseCatalog.forEach(e => {
      e.primary_muscles?.forEach(m => set.add(m));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [exerciseCatalog]);

  const uniqueEquipment = useMemo(() => {
    const set = new Set<string>();
    exerciseCatalog.forEach(e => { if (e.equipment) set.add(e.equipment); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [exerciseCatalog]);

  const filteredCatalog = useMemo(
    () => filterCatalog(exerciseCatalog, searchQuery, muscleFilters, equipmentFilters),
    [exerciseCatalog, searchQuery, muscleFilters, equipmentFilters]
  );

  // Reset state when drawer opens
  React.useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setMuscleFilters([]);
      setEquipmentFilters([]);
      setShowFilters(false);
      setShowCustomForm(false);
      setCustomName('');
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleCreateCustom = async () => {
    const trimmed = customName.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      const entry = await onCreateCustom(trimmed);
      if (entry) {
        onSelect(entry);
        setCustomName('');
        setShowCustomForm(false);
      }
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className='fixed inset-0 z-[60] flex flex-col items-center justify-end'>
        <div className='absolute inset-0 bg-black/50' onClick={onClose} />
        <div
          className={`relative w-full max-w-lg flex flex-col rounded-t-2xl ${theme.card} border-t border-x ${theme.border} shadow-2xl`}
          style={{ height: '90dvh', maxHeight: '90dvh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className='flex justify-center pt-3 pb-1'>
            <div className={`w-10 h-1 rounded-full ${theme.border} bg-current opacity-30`} />
          </div>

          {/* Header */}
          <div className={`flex items-center justify-between px-4 pb-3 border-b ${theme.border}`}>
            <h2 className='text-base font-semibold'>Agregar ejercicio</h2>
            <button
              type='button'
              onClick={onClose}
              className={`p-1.5 rounded-lg touch-manipulation ${theme.textSecondary} ${theme.hover}`}
              aria-label='Cerrar'
            >
              <X className='w-5 h-5' />
            </button>
          </div>

          {/* Search + filter + create button */}
          <div className='px-4 pt-3 pb-2 flex gap-2 items-center'>
            <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border ${theme.input}`}>
              <Search className={`w-4 h-4 flex-shrink-0 ${theme.textSecondary}`} />
              <input
                ref={searchInputRef}
                type='text'
                placeholder='Buscar ejercicio...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='flex-1 bg-transparent outline-none text-sm'
              />
            </div>
            <button
              type='button'
              onClick={() => setShowFilters(true)}
              className={`relative p-2.5 rounded-xl border touch-manipulation flex-shrink-0 ${
                totalFilters > 0 ? 'border-blue-500 text-blue-500' : `${theme.border} ${theme.hover}`
              }`}
              aria-label='Filtros'
            >
              <SlidersHorizontal className='w-4 h-4' />
              {totalFilters > 0 && (
                <span className='absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none'>
                  {totalFilters}
                </span>
              )}
            </button>
            <button
              type='button'
              onClick={() => setShowCustomForm(prev => !prev)}
              className='p-2.5 rounded-xl bg-blue-600 text-white touch-manipulation flex-shrink-0 hover:bg-blue-700 active:bg-blue-800'
              aria-label='Crear ejercicio personalizado'
            >
              <Plus className='w-4 h-4' />
            </button>
          </div>

          {/* Custom form (collapsible) */}
          {showCustomForm && (
            <div className={`px-4 pt-2 pb-3 border-b ${theme.border} space-y-2`}>
              <div className='flex items-center justify-between'>
                <p className={`text-xs ${theme.textSecondary}`}>
                  Crear ejercicio personalizado
                </p>
                <button
                  type='button'
                  onClick={() => { setShowCustomForm(false); setCustomName(''); }}
                  className={`p-1 rounded-lg touch-manipulation ${theme.textSecondary} ${theme.hover}`}
                  aria-label='Cerrar formulario'
                >
                  <X className='w-3.5 h-3.5' />
                </button>
              </div>
              <div className='flex gap-2 items-center'>
                <input
                  type='text'
                  placeholder='Nombre del ejercicio...'
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value.slice(0, 50))}
                  maxLength={50}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCustom(); }}
                  className={`flex-1 px-3 py-2 rounded-xl border ${theme.input} text-sm`}
                />
                <button
                  type='button'
                  onClick={handleCreateCustom}
                  disabled={!customName.trim() || creating}
                  className='px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:bg-blue-800 touch-manipulation disabled:opacity-50'
                >
                  {creating ? '...' : 'Crear'}
                </button>
              </div>
            </div>
          )}

          {/* Catalog list */}
          <div className='flex-1 overflow-y-auto overscroll-contain px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+1rem)]'>
            {filteredCatalog.length === 0 ? (
              <p className={`text-sm text-center py-8 ${theme.textSecondary}`}>
                No se encontraron ejercicios
              </p>
            ) : (
              filteredCatalog.map((entry) => (
                <div key={entry.id} className={`flex items-center gap-3 py-3 border-b ${theme.border}`}>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-semibold truncate'>{entry.name}</p>
                    <div className='flex flex-wrap gap-1 mt-1'>
                      {entry.primary_muscles?.map(m => (
                        <span key={m} className='px-1.5 py-0.5 rounded text-[10px] bg-blue-600/15 text-blue-500 font-medium'>
                          {m}
                        </span>
                      ))}
                      {entry.equipment && (
                        <span className='px-1.5 py-0.5 rounded text-[10px] bg-emerald-600/15 text-emerald-500 font-medium'>
                          {entry.equipment}
                        </span>
                      )}
                      {entry.owner_user_id && (
                        <span className='px-1.5 py-0.5 rounded text-[10px] bg-purple-600/15 text-purple-400 font-medium'>
                          Personal
                        </span>
                      )}
                    </div>
                  </div>
                  <ExerciseDemoButton name={entry.name} className='w-8 h-8 rounded-full' />
                  <button
                    type='button'
                    onClick={() => onSelect(entry)}
                    className='w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 hover:bg-blue-700 active:bg-blue-800 touch-manipulation'
                    aria-label={`Agregar ${entry.name}`}
                  >
                    <Plus className='w-4 h-4' />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Filter view on top of drawer */}
      <ExerciseFilterView
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={(muscles, equipment) => {
          setMuscleFilters(muscles);
          setEquipmentFilters(equipment);
          setShowFilters(false);
        }}
        initialMuscles={muscleFilters}
        initialEquipment={equipmentFilters}
        availableMuscles={uniqueMuscles}
        availableEquipment={uniqueEquipment}
      />
    </>
  );
}
